/**
 * CoverageMapView — Leaflet map for the GeoLink Coverage Analysis page.
 *
 * Three render modes:
 *   - heat:     leaflet.heat layer weighted by job_count
 *   - clusters: CircleMarker per point, radius scaled by job_count,
 *               colour by job_count quartile
 *   - dots:     small indigo CircleMarker per point (no scaling)
 *
 * Auto-fits the map bounds to the supplied points whenever the points
 * change. Points without resolved lat/lng are silently skipped — the
 * coverage page lists them in the side rail regardless.
 */

import { useEffect, useMemo } from 'react';
import L from 'leaflet';
import 'leaflet.heat';
// leaflet.heat attaches itself to L as a side effect; access via (L as any).heatLayer
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from 'react-leaflet';
import type { CoveragePointDto } from '../../../api/geolink';
import styles from './CoverageMapView.module.css';

export type CoverageViewMode = 'heat' | 'clusters' | 'dots';

export interface CoverageMapViewProps {
  points: CoveragePointDto[];
  viewMode: CoverageViewMode;
  loading?: boolean;
}

const DEFAULT_CENTER: [number, number] = [54.0, -2.5]; // UK
const DEFAULT_ZOOM = 6;

function withCentroid(p: CoveragePointDto): p is CoveragePointDto & { latitude: number; longitude: number } {
  return p.latitude !== null && p.longitude !== null;
}

/**
 * Quartile cut points for colour assignment in cluster mode. We use four
 * fixed buckets: light green / yellow / orange / red.
 */
function quartileColour(jobCount: number, quartiles: [number, number, number]): string {
  if (jobCount > quartiles[2]) return '#dc2626'; // red
  if (jobCount > quartiles[1]) return '#f97316'; // orange
  if (jobCount > quartiles[0]) return '#eab308'; // yellow
  return '#10b981'; // green
}

function FitBounds({ points }: { points: CoveragePointDto[] }) {
  const map = useMap();
  useEffect(() => {
    const withLatLng = points.filter(withCentroid);
    if (withLatLng.length === 0) return;
    const bounds = L.latLngBounds(
      withLatLng.map((p) => [p.latitude, p.longitude] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
  }, [map, points]);
  return null;
}

function HeatLayerOverlay({ points }: { points: CoveragePointDto[] }) {
  const map = useMap();
  useEffect(() => {
    const withLatLng = points.filter(withCentroid);
    if (withLatLng.length === 0) return undefined;
    const maxCount = withLatLng.reduce((m, p) => Math.max(m, p.job_count), 1);
    const data = withLatLng.map(
      (p) => [p.latitude, p.longitude, p.job_count / maxCount] as [number, number, number],
    );
    const layer = (L as any).heatLayer(data, {
      radius: 28,
      blur: 22,
      maxZoom: 12,
      minOpacity: 0.35,
      gradient: {
        '0.2': '#10b981',
        '0.5': '#eab308',
        '0.8': '#f97316',
        '1.0': '#dc2626',
      },
    });
    layer.addTo(map);
    return () => {
      map.removeLayer(layer);
    };
  }, [map, points]);
  return null;
}

function PointPopup({ point }: { point: CoveragePointDto }) {
  return (
    <Popup>
      <p className={styles.popupTitle}>{point.outcode}</p>
      <p className={styles.popupMeta}>
        {point.job_count} job{point.job_count === 1 ? '' : 's'}
        <br />
        £{point.total_revenue.toFixed(2)} total · £{point.avg_price.toFixed(2)} avg
        <br />
        Source: {point.source}
      </p>
    </Popup>
  );
}

function ClusterLayer({ points }: { points: CoveragePointDto[] }) {
  const withLatLng = useMemo(() => points.filter(withCentroid), [points]);
  const quartiles = useMemo<[number, number, number]>(() => {
    if (withLatLng.length === 0) return [0, 0, 0];
    const counts = withLatLng.map((p) => p.job_count).sort((a, b) => a - b);
    const q = (frac: number): number => counts[Math.floor(counts.length * frac)] ?? 0;
    return [q(0.25), q(0.5), q(0.75)];
  }, [withLatLng]);

  const maxCount = useMemo(
    () => withLatLng.reduce((m, p) => Math.max(m, p.job_count), 1),
    [withLatLng],
  );

  return (
    <>
      {withLatLng.map((p) => {
        const ratio = p.job_count / maxCount;
        const radius = 8 + Math.round(ratio * 32);
        const colour = quartileColour(p.job_count, quartiles);
        return (
          <CircleMarker
            key={p.outcode}
            center={[p.latitude, p.longitude]}
            radius={radius}
            pathOptions={{
              color: colour,
              fillColor: colour,
              fillOpacity: 0.55,
              weight: 1.5,
            }}
          >
            <PointPopup point={p} />
          </CircleMarker>
        );
      })}
    </>
  );
}

function DotsLayer({ points }: { points: CoveragePointDto[] }) {
  const withLatLng = useMemo(() => points.filter(withCentroid), [points]);
  return (
    <>
      {withLatLng.map((p) => (
        <CircleMarker
          key={p.outcode}
          center={[p.latitude, p.longitude]}
          radius={5}
          pathOptions={{
            color: '#4338ca',
            fillColor: '#6366f1',
            fillOpacity: 0.6,
            weight: 1,
          }}
        >
          <PointPopup point={p} />
        </CircleMarker>
      ))}
    </>
  );
}

export function CoverageMapView({ points, viewMode, loading = false }: CoverageMapViewProps) {
  const hasPoints = points.some(withCentroid);

  if (!loading && !hasPoints) {
    return (
      <div className={styles.empty}>
        No coverage points to plot yet. Outcode lookups happen on demand —
        try again after running an initial coverage query.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} className={styles.map} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {viewMode === 'heat' && <HeatLayerOverlay points={points} />}
        {viewMode === 'clusters' && <ClusterLayer points={points} />}
        {viewMode === 'dots' && <DotsLayer points={points} />}
      </MapContainer>
    </div>
  );
}
